"use client";

import React, { createContext, useContext, useState, useCallback, useMemo } from "react";
import { reactionsApi, ReactionType, REACTION_TYPES } from "@/lib/api/public-client";
import { usePublicAuth } from "@/lib/auth/PublicAuthContext";
interface ReactionItem {
  slug: string;
  category?: string;
  title?: string;
  thumbnail_url?: string;
  description?: string;
  article_url?: string;
}
interface ReactionsContextType {
  // Get user's reaction on a story (like, dislike, or null)
  getReaction: (slug: string) => ReactionType | null;
  // Check if user liked a story
  isLiked: (slug: string) => boolean;
  // Check if user disliked a story
  isDisliked: (slug: string) => boolean;
  // Toggle like status
  toggleLike: (item: ReactionItem) => Promise<boolean>;
  // Toggle dislike status
  toggleDislike: (item: ReactionItem) => Promise<boolean>;
  // Loading state for a specific slug
  isLoading: (slug: string) => boolean;
}
const ReactionsContext = createContext<ReactionsContextType | null>(null);
export function ReactionsProvider({
  children
}: {
  children: React.ReactNode;
}) {
  const {
    isAuthenticated
  } = usePublicAuth();
  // Map of slug -> reaction type (like, dislike, or null for no reaction)
  const [reactions, setReactions] = useState<Map<string, ReactionType | null>>(new Map());
  // Set of slugs currently loading
  const [loadingSet, setLoadingSet] = useState<Set<string>>(new Set());

  // Get reaction for a slug
  const getReaction = useCallback((slug: string): ReactionType | null => {
    return reactions.get(slug) ?? null;
  }, [reactions]);

  // Check if liked
  const isLiked = useCallback((slug_0: string): boolean => {
    return reactions.get(slug_0) === REACTION_TYPES.LIKE;
  }, [reactions]);

  // Check if disliked
  const isDisliked = useCallback((slug_1: string): boolean => {
    return reactions.get(slug_1) === REACTION_TYPES.DISLIKE;
  }, [reactions]);

  // Check if loading
  const isLoadingFn = useCallback((slug_2: string): boolean => {
    return loadingSet.has(slug_2);
  }, [loadingSet]);

  // Toggle like with optimistic update
  const toggleLike = useCallback(async (item: ReactionItem): Promise<boolean> => {
    if (!isAuthenticated) {
      return false;
    }
    const currentReaction = reactions.get(item.slug);
    const wasLiked = currentReaction === REACTION_TYPES.LIKE;

    // Set loading
    setLoadingSet(prev => new Set(prev).add(item.slug));

    // Optimistic update
    setReactions(prev_0 => {
      const next = new Map(prev_0);
      if (wasLiked) {
        next.set(item.slug, null);
      } else {
        next.set(item.slug, REACTION_TYPES.LIKE);
      }
      return next;
    });
    try {
      const result = await reactionsApi.toggleLike({
        slug: item.slug,
        category: item.category,
        title: item.title,
        thumbnail_url: item.thumbnail_url,
        description: item.description,
        article_url: item.article_url
      });

      // Sync with server response
      setReactions(prev_2 => {
        const next_1 = new Map(prev_2);
        next_1.set(item.slug, result.liked ? REACTION_TYPES.LIKE : null);
        return next_1;
      });
      return result.liked;
    } catch (error) {
      // Revert optimistic update on error
      setReactions(prev_1 => {
        const next_0 = new Map(prev_1);
        next_0.set(item.slug, currentReaction ?? null);
        return next_0;
      });
      console.error("Failed to toggle like:", error);
      throw error;
    } finally {
      setLoadingSet(prev => {
        const next = new Set(prev);
        next.delete(item.slug);
        return next;
      });
    }
  }, [isAuthenticated, reactions]);

  // Toggle dislike with optimistic update
  const toggleDislike = useCallback(async (item_0: ReactionItem): Promise<boolean> => {
    if (!isAuthenticated) {
      return false;
    }
    const currentReaction_0 = reactions.get(item_0.slug);
    const wasDisliked = currentReaction_0 === REACTION_TYPES.DISLIKE;

    // Set loading
    setLoadingSet(prev_3 => new Set(prev_3).add(item_0.slug));

    // Optimistic update
    setReactions(prev_4 => {
      const next_2 = new Map(prev_4);
      if (wasDisliked) {
        next_2.set(item_0.slug, null);
      } else {
        next_2.set(item_0.slug, REACTION_TYPES.DISLIKE);
      }
      return next_2;
    });
    try {
      const result_0 = await reactionsApi.toggleDislike({
        slug: item_0.slug,
        category: item_0.category,
        title: item_0.title,
        thumbnail_url: item_0.thumbnail_url,
        description: item_0.description,
        article_url: item_0.article_url
      });

      // Sync with server response
      setReactions(prev_6 => {
        const next_4 = new Map(prev_6);
        next_4.set(item_0.slug, result_0.disliked ? REACTION_TYPES.DISLIKE : null);
        return next_4;
      });
      return result_0.disliked;
    } catch (error_0) {
      // Revert optimistic update on error
      setReactions(prev_5 => {
        const next_3 = new Map(prev_5);
        next_3.set(item_0.slug, currentReaction_0 ?? null);
        return next_3;
      });
      console.error("Failed to toggle dislike:", error_0);
      throw error_0;
    } finally {
      setLoadingSet(prev => {
        const next = new Set(prev);
        next.delete(item_0.slug);
        return next;
      });
    }
  }, [isAuthenticated, reactions]);
  const value = useMemo(() => ({
    getReaction,
    isLiked,
    isDisliked,
    toggleLike,
    toggleDislike,
    isLoading: isLoadingFn
  }), [getReaction, isLiked, isDisliked, toggleLike, toggleDislike, isLoadingFn]);
  return <ReactionsContext.Provider value={value}>
      {children}
    </ReactionsContext.Provider>;
}
export function useReactions() {
  const context = useContext(ReactionsContext);
  if (!context) {
    throw new Error("useReactions must be used within a ReactionsProvider");
  }
  return context;
}

// Safe hook that doesn't throw if used outside provider
export function useReactionsSafe() {
  return useContext(ReactionsContext);
}